import { useState, useEffect, useMemo } from 'react';

interface UsePaginatedTableOptions<T> {
  items: T[];
  initialItemsPerPage?: number;
}

interface UsePaginatedTableReturn<T> {
  paginatedItems: T[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  goToPage: (page: number) => void;
  setItemsPerPage: (count: number) => void;
  resetPagination: () => void;
}

export function usePaginatedTable<T>({
  items,
  initialItemsPerPage = 10,
}: UsePaginatedTableOptions<T>): UsePaginatedTableReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPageState] = useState(() => {
    const saved = localStorage.getItem('admin_table_items_per_page');
    return saved ? parseInt(saved, 10) : initialItemsPerPage;
  });

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const setItemsPerPage = (count: number) => {
    setItemsPerPageState(count);
    localStorage.setItem('admin_table_items_per_page', count.toString());
    setCurrentPage(1);
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  return {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    goToPage,
    setItemsPerPage,
    resetPagination,
  };
}
